import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useEnvStore, type Environment } from '@/lib/store/envStore';
import { cn } from '@/lib/utils/cn';
import { Button } from '@/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/ui/dialog';
import { StatusDot } from '@/ui/status-dot';

interface EnvSwitcherProps {
    className?: string;
}

/**
 * Segmented control Test / Live. Always visible in Topbar.
 * Confirmation when switching Test → Live the first time per session.
 */
export function EnvSwitcher({ className }: EnvSwitcherProps) {
    const env = useEnvStore((state) => state.env);
    const setEnv = useEnvStore((state) => state.setEnv);
    const queryClient = useQueryClient();
    const [pendingSwitch, setPendingSwitch] = useState<Environment | null>(null);
    const [acknowledgedLive, setAcknowledgedLive] = useState(false);

    const applySwitch = (target: Environment) => {
        if (target === env) return;
        if (target === 'live' && !acknowledgedLive) {
            setPendingSwitch(target);
            return;
        }
        setEnv(target);
        queryClient.clear();
    };

    const confirmLiveSwitch = () => {
        setAcknowledgedLive(true);
        setEnv('live');
        queryClient.clear();
        setPendingSwitch(null);
    };

    return (
        <>
            <div
                role="group"
                aria-label="Environment"
                className={cn(
                    'inline-flex items-center rounded-md border border-border bg-card p-0.5',
                    className,
                )}
            >
                <EnvButton current={env} value="test" onClick={() => applySwitch('test')} />
                <EnvButton current={env} value="live" onClick={() => applySwitch('live')} />
            </div>

            <Dialog
                open={pendingSwitch === 'live'}
                onOpenChange={(open) => !open && setPendingSwitch(null)}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Switching to Live environment</DialogTitle>
                        <DialogDescription>
                            Live API keys access production data and trigger real settlements.
                            Operations performed in this environment are billable and visible to
                            travellers.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setPendingSwitch(null)}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={confirmLiveSwitch}>
                            Switch to Live
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

interface EnvButtonProps {
    current: Environment;
    value: Environment;
    onClick: () => void;
}

function EnvButton({ current, value, onClick }: EnvButtonProps) {
    const isActive = current === value;
    const tone = value === 'test' ? 'gold' : 'success';
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={isActive}
            className={cn(
                'inline-flex h-7 items-center gap-1.5 rounded px-2.5 text-xs font-medium transition-colors duration-fast',
                isActive
                    ? 'bg-muted text-foreground'
                    : 'text-muted-foreground hover:text-foreground',
            )}
        >
            <StatusDot tone={tone} />
            <span className="capitalize">{value}</span>
        </button>
    );
}
